import React, {useState, useEffect, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {ExplainerSection} from './sections/ExplainerSection';
import {PrerequisitesSection} from './sections/PrerequisitesSection';
import {SetupGuidesSection} from './sections/SetupGuidesSection';
import {ConnectAssistantSection} from './sections/ConnectAssistantSection';
import {Step} from './sections/SetupGuideCard';
import {OfficialPromptsSection} from './sections/OfficialPromptsSection';
import {PlaygroundSection} from './sections/PlaygroundSection';
import {FaIcon} from '../FaIcon';
import {LocalStorage} from '../../utils/LocalStorage';
import {usePreviewContext} from '../../context/PreviewContext';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {copyStatusAfterCopy, fetchPromptCopyStatus} from '../prompts/promptCopyPing';
import {PortalPrivacyNotice} from '../portal_usage/PortalPrivacyNotice';
import {PortalPromptChips} from '../portal/PortalPromptChips';
import {usePortalClient} from '../../context/PortalClientContext';

// Identifier del pack completo multi-cliente. Es CLAVE PÚBLICA congelada (la resuelven también
// el MCP server, Connect y los skills), y acá queda sólo como camino de fallback: la resolución
// buena es por modalidad.
const AGENCY_FULL_IDENTIFIER = '[Dashbo] Analista senior (Agency)';

/**
 * Trae las modalidades multi-cliente del prompt oficial: el pack completo (Project/GPT nuevo),
 * el bloque aditivo (el GPT ya tiene instrucciones propias) y el stub del canal pull (bloque
 * corto que delega el cuerpo operativo en el conector — F3).
 *
 * El backend viejo IGNORA `?audience=` y devuelve los ~55 oficiales sin `variant`, así que el
 * filtro por variante no matchea nada y caemos al fetch por identifier — o sea, exactamente el
 * comportamiento de hoy. Ese fallback no es defensivo de más: backend y frontend no deployan
 * juntos, y sin él la pantalla se quedaría sin prompt en la ventana intermedia. Un backend
 * nuevo sin la fila stub sembrada devuelve `stub: null` y la UI ofrece dos opciones como hoy.
 */
export function fetchAgencyPrompts() {
    const listUrl = `${API_URL}agent/prompts/official/?audience=AGENCY_WIDE&include_library=false`;
    // Cada variante viaja como {id, text, version}: el id alimenta el ping de copiado y la
    // versión el chip "v9 disponible". Con un backend viejo (sin id/current_version) quedan
    // en null y las superficies degradan a solo-texto — sin ping ni chip.
    const asPromptData = prompt => ({
        id: prompt.id || null,
        text: prompt.prompt_text,
        version: prompt.current_version || null,
    });
    return new APIClient(listUrl).call('GET').then(response => {
        const prompts = (response && response.prompts) || [];
        const byVariant = variant => {
            const prompt = prompts.find(p => p.variant === variant);
            return prompt ? asPromptData(prompt) : null;
        };
        const full = byVariant('FULL');
        if (full) {
            return {full, addon: byVariant('ADDON'), stub: byVariant('STUB')};
        }
        const detailUrl = `${API_URL}agent/prompts/official/${AGENCY_FULL_IDENTIFIER}/`;
        return new APIClient(detailUrl).call('GET').then(detail => ({
            full: detail && detail.prompt ? asPromptData(detail.prompt) : null,
            addon: null,
            stub: null,
        }));
    });
}

/**
 * El pack de instrucciones del CLIENTE FINAL del portal (`/portal/me/starter-pack/`):
 * el prompt le habla al cliente, fija su alcance server-side (un cliente, o el bloque
 * combinado si accede a varios) y solo promete tools que un usuario-cliente puede usar.
 *
 * Con `previewClientId` es el MISMO pack visto por la agencia en «Ver portal como cliente»
 * (`portal/preview/<id>/starter-pack/`, mismo shape, alcance de ese cliente). Hasta el 30/09 la
 * vista previa pedía el prompt de la AGENCIA y mostraba algo que el cliente no recibe nunca
 * (card E5J1Cqzu).
 *
 * Devuelve null si falla o si el backend todavía no lo expone: la pantalla se queda sin prompt que
 * copiar, que es el estado «no disponible» de siempre. NUNCA cae al prompt de la agencia: ese texto
 * habla de «los clientes de la agencia» y no es el del cliente.
 */
export function fetchPortalStarterPack(previewClientId = null) {
    const asPromptData = (markdown, meta) => ({
        id: (meta && meta.id) || null,
        text: markdown,
        version: (meta && meta.version) || null,
    });
    const url = previewClientId
        ? `${API_URL}portal/preview/${encodeURIComponent(previewClientId)}/starter-pack/`
        : `${API_URL}portal/me/starter-pack/`;
    return new APIClient(url).call('GET')
        .then(response => {
            if (!response || !response.full_markdown) return null;
            return {
                full: asPromptData(response.full_markdown, response.full_prompt),
                addon: response.addon_markdown
                    ? asPromptData(response.addon_markdown, response.addon_prompt)
                    : null,
                stub: null,
            };
        })
        .catch(() => null);
}

/**
 * «Configuración avanzada», plegada por defecto: lo que necesita quien integra (API, Make, n8n,
 * Python), la consulta de prueba y el prompt completo. Nada de esto desapareció de la pantalla: se
 * mudó acá abajo para que no tape el «conecta tu asistente» de arriba (card eUkO5X6S).
 */
// Si «Configuración avanzada» quedó abierta en esta pestaña: el «Volver» desde el informe de ejemplo
// (`/dashbo-mcp/demo-report`) tiene que encontrarla como la dejó (review C-…-24).
export const ADVANCED_OPEN_STORAGE_KEY = 'dashbo_mcp_advanced_open';

function readAdvancedOpen() {
    try {
        return sessionStorage.getItem(ADVANCED_OPEN_STORAGE_KEY) === '1';
    } catch {
        return false;
    }
}

function writeAdvancedOpen(open) {
    try {
        sessionStorage.setItem(ADVANCED_OPEN_STORAGE_KEY, open ? '1' : '0');
    } catch { /* sin sessionStorage sólo se pierde recordar la sección abierta */ }
}

/**
 * `children` puede ser una función que recibe `seen`: si la sección se abrió alguna vez en esta
 * visita. La usa la consulta de prueba para no pedir datos mientras nadie la mira (card k14eI6RC):
 * montada y oculta, pedía la lista de clientes y sus campos en cada visita a la página, y en el portal
 * esas dos llamadas contaban como uso del asistente del cliente — «Activo» y «+2 pasos» para alguien
 * que sólo entró a mirar cómo conectar su IA.
 */
function AdvancedSection({isClientView, children}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(readAdvancedOpen);
    // Una vez abierta queda «vista» aunque se pliegue: lo de adentro sigue montado (ver abajo).
    const [seen, setSeen] = useState(open);
    const toggle = () => {
        writeAdvancedOpen(!open);
        if (!open) setSeen(true);
        setOpen(!open);
    };
    return (
        <section className={`dashbo-mcp-section mcp-advanced${open ? ' -open' : ''}`}>
            <button type="button" className="mcp-advanced__toggle" aria-expanded={open} onClick={toggle}>
                <FaIcon iconName={open ? 'chevron-up' : 'chevron-down'}/>
                <span className="mcp-advanced__title">{t('dashbo_mcp.advanced.title')}</span>
                <span className="mcp-advanced__tag">{t('dashbo_mcp.advanced.tag')}</span>
            </button>
            <p className="mcp-advanced__subtitle">
                {t(isClientView ? 'dashbo_mcp.client.advanced_subtitle' : 'dashbo_mcp.advanced.subtitle')}
            </p>
            {/* Montada y oculta, no desmontada: cerrarla no se lleva lo escrito en la consulta de prueba. */}
            <div className="mcp-advanced__body" hidden={!open}>
                {typeof children === 'function' ? children(seen) : children}
            </div>
        </section>
    );
}

/**
 * Un proyecto de Claude o ChatGPT dedicado a un cliente, con el prompt completo en sus
 * instrucciones. El paso a paso es el que pedía la card psUDM7lZ: dónde se pega (las «Instrucciones
 * del proyecto», no el contexto ni la memoria), porque sin eso nadie lo encontraba.
 */
function ProjectSection({prompts, copyStatus, onCopied, isClientView}) {
    const {t} = useTranslation();
    if (!prompts || !prompts.full || !prompts.full.text) return null;
    // El cliente del portal no trabaja «sobre un cliente»: su proyecto es el de sus propios datos.
    const ns = isClientView ? 'dashbo_mcp.client.project' : 'dashbo_mcp.project';
    return (
        <div className="dashbo-mcp-section">
            <h3 className="section-title">{t(`${ns}.title`)}</h3>
            <p className="section-description">{t(`${ns}.description`)}</p>
            <div className="setup-guide-card mcp-connect__card">
                <div className="card-body">
                    {['step1', 'step2', 'step3'].map((key, i) => (
                        <Step key={key} number={i + 1}>
                            <span dangerouslySetInnerHTML={{__html: t(`${ns}.${key}`)}}/>
                        </Step>
                    ))}
                </div>
            </div>
            <OfficialPromptsSection prompts={prompts} copyStatus={copyStatus} onCopied={onCopied}/>
        </div>
    );
}

export function DashboMCPContent() {
    const {t} = useTranslation();
    const {isPreview, previewClientId} = usePreviewContext();
    const portalClient = usePortalClient();
    const [prompts, setPrompts] = useState(null);
    const [copyStatus, setCopyStatus] = useState({});

    // Usuario del portal REAL (no la agencia mirando la vista previa).
    const isPortalClientUser = !isPreview && LocalStorage.isClientUser();

    useEffect(() => {
        let cancelled = false;
        // El cliente final —y la agencia en «Ver portal como cliente»— ven el prompt DEL CLIENTE
        // (mono o combinado según sus clientes). El de la agencia («resolvé de qué cliente se
        // trata…») sólo en la pantalla de la agencia: ni la vista previa ni el portal caen a él
        // cuando el pack falla (card E5J1Cqzu).
        let request;
        if (isPreview) {
            request = previewClientId ? fetchPortalStarterPack(previewClientId) : Promise.resolve(null);
        } else if (isPortalClientUser) {
            request = fetchPortalStarterPack();
        } else {
            request = fetchAgencyPrompts();
        }
        request.then(pack => {
            if (!cancelled) setPrompts(pack);
        });
        fetchPromptCopyStatus().then(status => {
            if (!cancelled) setCopyStatus(status);
        });
        return () => {
            cancelled = true;
        };
    }, [isPreview, previewClientId, isPortalClientUser]);

    // Actualización optimista tras copiar (helper único — las reglas viven con él).
    const handleCopied = useCallback((promptId, version) => {
        setCopyStatus(prev => copyStatusAfterCopy(prev, promptId, version));
    }, []);

    let autoClientId = null;
    if (isPreview && previewClientId) {
        autoClientId = previewClientId;
    } else if (LocalStorage.isClientUser()) {
        autoClientId = portalClient.activeClientId ? String(portalClient.activeClientId) : LocalStorage.fetchUserClientId();
    }

    // Modo cliente: el cliente del portal (o la agencia viendo el preview "Ver como cliente").
    // En este modo el copy se enfoca en "tu agencia ya conectó tus datos" y se oculta la sección
    // de Prerequisitos (configuración de conexiones, que es operación de agencia).
    const isClientView = isPreview || LocalStorage.isClientUser();

    return (
        <div className="dashbo-mcp-page">
            <header className="dashbo-mcp-hero">
                <div className="dashbo-mcp-hero-content">
                    <div className="dashbo-mcp-hero-icon">
                        <FaIcon iconName="bolt"/>
                    </div>
                    <div className="dashbo-mcp-hero-text">
                        <h1>{t(isClientView ? 'dashbo_mcp.client.hero_title' : 'dashbo_mcp.hero_title')}</h1>
                        <p>{t(isClientView ? 'dashbo_mcp.client.hero_subtitle' : 'dashbo_mcp.hero_subtitle')}</p>
                    </div>
                </div>
            </header>

            <div className="section-body">
                {/* Arriba, conectar el asistente (card eUkO5X6S). Todo lo técnico —qué es el MCP,
                    token y URLs, Make/n8n/Python, la consulta de prueba y el prompt completo— va
                    en «Configuración avanzada», plegada, al pie. */}
                <ConnectAssistantSection prompts={prompts} copyStatus={copyStatus} onCopied={handleCopied}/>
                {/* La otra cara del aviso que ve la agencia en la ficha del cliente. Va acá, en el
                    momento en que el cliente conecta su IA —el del consentimiento— y no enterrado
                    en unos términos. Como `isClientView` incluye el preview, también aparece en
                    "Ver portal como cliente": la agencia ve exactamente lo que ve su cliente, que
                    es la mitad del punto. Los dos avisos salen en el mismo cambio: no se puede
                    mostrar las preguntas sin decírselo a quien las escribe. */}
                {isClientView && <PortalPrivacyNotice variant="client"/>}
                {!isClientView && <PrerequisitesSection/>}
                {/* Sólo para el usuario REAL del portal: discovery scopea por SU token, así que en
                    el preview de agencia (token de agencia) el resultado no representaría lo que
                    ve el cliente. Si no hay prompts publicados, el componente no renderiza nada.
                    El `clientId` sale del hook del portal, que ya resuelve el caso de un solo
                    cliente (y el escalar legacy): con varios clientes los chips tienen que hablar
                    del espacio ACTIVO y refetchear al cambiarlo, no de un primario que elige el
                    backend. Si todavía no hay activo, el componente no pide nada — a diferencia del
                    playground, acá inventar un cliente sale caro: el chip se copia y se le pide a
                    la IA un prompt del cliente equivocado. */}
                {isPortalClientUser && <PortalPromptChips clientId={portalClient.activeClientId}/>}
                <AdvancedSection isClientView={isClientView}>
                    {(seen) => (
                        <>
                            <ExplainerSection isClientView={isClientView}/>
                            <SetupGuidesSection isClientView={isClientView}/>
                            <PlaygroundSection
                                // No pide nada hasta que la sección se abre (card k14eI6RC).
                                active={seen}
                                autoClientId={autoClientId}
                                isClientView={isClientView}
                                // Multi-cliente: el selector se muestra (limitado a SUS clientes — el
                                // endpoint ya scopea al M2M del usuario) con el activo preseleccionado.
                                allowClientSelection={isPortalClientUser && portalClient.isMultiClient}
                                connectionsPath={isPortalClientUser ? '/portal/connections' : null}
                            />
                            <ProjectSection prompts={prompts} copyStatus={copyStatus} onCopied={handleCopied}
                                            isClientView={isClientView}/>
                        </>
                    )}
                </AdvancedSection>
            </div>
        </div>
    );
}
